import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useData } from "../../hooks/useData";
import GlowButton from "../ui-custom/GlowButton";
import Marquee from "../ui-custom/Marquee";
import { stagger, lineUp } from "../ui-custom/Reveal";

export default function Hero() {
  const { data } = useData("hero");
  const { data: links } = useData("links");
  const [idx, setIdx] = useState(0);
  const layerA = useRef(null);
  const layerB = useRef(null);

  useEffect(() => {
    if (!data) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % data.slides.length), 5000);
    return () => clearInterval(t);
  }, [data]);

  useEffect(() => {
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      if (layerA.current) layerA.current.style.transform = `translate(${x * 16}px, ${y * 16}px)`;
      if (layerB.current) layerB.current.style.transform = `translate(${x * -24}px, ${y * -24}px)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  if (!data) return <div className="h-screen" />;
  const slide = data.slides[idx];
  const words = slide.title.split(" ");

  return (
    <section className="relative flex min-h-[90vh] items-center overflow-hidden px-5 pt-16 pb-10" data-testid="hero-section">
      <div className="mx-auto grid w-full max-w-7xl items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-2"
            data-testid="hero-eyebrow"
          >
            <Sparkles size={14} /> {data.eyebrow}
          </motion.span>

          <motion.h1
            key={slide.title} variants={stagger} initial="hidden" animate="show"
            className="mt-6 text-4xl font-bold leading-[1.05] text-slate-900 sm:text-5xl lg:text-6xl"
            data-testid="hero-heading"
          >
            {words.map((w, i) => (
              <span key={i} className="mr-[0.25em] inline-block overflow-hidden align-bottom">
                <motion.span variants={lineUp} className="inline-block">
                  {["Smart", "Digital"].includes(w) ? <span className="text-gradient">{w}</span> : w}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            key={slide.subtitle}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-6 max-w-lg text-lg text-slate-500" data-testid="hero-subtitle"
          >
            {slide.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <GlowButton href={links?.register} to={!links ? data.primaryCta.path : undefined} icon={<ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />} data-testid="hero-primary-cta">
              {data.primaryCta.label}
            </GlowButton>
            <GlowButton to={data.secondaryCta.path} variant="ghost" data-testid="hero-secondary-cta">
              {data.secondaryCta.label}
            </GlowButton>
          </motion.div>

          <div className="mt-10 flex gap-2">
            {data.slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} data-testid={`hero-dot-${i}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-brand" : "w-3 bg-slate-300"}`} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>
        </div>

        <div className="relative h-[420px] sm:h-[500px]">
          <div ref={layerA} className="absolute inset-0 transition-transform duration-300 ease-out">
            <motion.div
              key={slide.image}
              initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }}
              className="floaty relative h-full overflow-hidden rounded-[2rem] grad-border glow-brand"
            >
              <img src={slide.image} alt={slide.title} loading="eager" className="h-full w-full object-cover" />
            </motion.div>
          </div>

          <div ref={layerB} className="pointer-events-none absolute inset-0 transition-transform duration-300 ease-out">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              className="absolute -left-4 top-10 glass-strong rounded-2xl px-4 py-3">
              <p className="text-xs text-slate-500">Instant PAN</p>
              <p className="text-lg font-bold text-brand-2">Approved ✓</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
              className="absolute -right-2 bottom-16 glass-strong rounded-2xl px-4 py-3">
              <p className="text-xs text-slate-500">Wallet</p>
              <p className="text-lg font-bold text-brand">₹1,24,580</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.1 }}
              className="absolute right-8 top-4 flex h-14 w-14 items-center justify-center rounded-2xl glass-strong text-brand-2">
              <Sparkles />
            </motion.div>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-4 left-0 w-full opacity-90">
        <Marquee items={data.marquee} />
      </div>
    </section>
  );
}
